<template>
  <div class="media-clean bg-white min-h-screen">
    <!-- Header -->
    <div class="bg-white/90 backdrop-blur-sm shadow-2xl border border-black/20 rounded-lg mx-4 my-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 class="text-3xl font-bold text-black mb-2">Media Upload</h1>
        <p class="text-gray-600">Upload and manage your media files with ease</p>
      </div>
    </div>

    <!-- Upload Area -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
        <div class="border-2 border-dashed border-black/30 rounded-lg p-12 text-center hover:border-black/50 transition-colors duration-300">
          <div class="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
            <span class="text-white font-bold text-2xl">📸</span>
          </div>
          <h3 class="text-xl font-semibold text-black mb-2">Drop files here or click to upload</h3>
          <p class="text-gray-600 mb-4">Support for images, videos, and documents</p>
          <input
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx"
            @change="handleFileUpload"
            class="hidden"
            id="file-upload"
          />
          <button
            @click="$refs.fileInput?.click()"
            class="px-6 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200"
          >
            Choose Files
          </button>
        </div>
      </div>

      <!-- File List -->
      <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6">
        <h3 class="text-xl font-semibold text-black mb-4">Uploaded Files</h3>
        <div v-if="files.length === 0" class="text-center py-12">
          <p class="text-gray-500">No files uploaded yet</p>
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="file in files"
            :key="file.id"
            class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-black/10 hover:bg-gray-100 transition-colors duration-200"
          >
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <span class="text-white font-bold text-sm">{{ getFileIcon(file.type) }}</span>
              </div>
              <div>
                <p class="font-medium text-black">{{ file.name }}</p>
                <p class="text-sm text-gray-600">{{ formatFileSize(file.size) }} • {{ formatDate(file.uploadedAt) }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="downloadFile(file)"
                class="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors duration-200"
              >
                Download
              </button>
              <button
                @click="deleteFile(file.id)"
                class="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Storage Info -->
      <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6">
        <h3 class="text-xl font-semibold text-black mb-4">Storage Information</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="text-center">
            <p class="text-2xl font-bold text-black">{{ formatFileSize(totalSize) }}</p>
            <p class="text-sm text-gray-600">Total Storage Used</p>
          </div>
          <div class="text-center">
            <p class="text-2xl font-bold text-black">{{ files.length }}</p>
            <p class="text-sm text-gray-600">Files Uploaded</p>
          </div>
          <div class="text-center">
            <p class="text-2xl font-bold text-black">10 GB</p>
            <p class="text-sm text-gray-600">Storage Limit</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const files = ref([])
const fileInput = ref(null)

const handleFileUpload = (event) => {
  const uploadedFiles = Array.from(event.target.files)
  
  uploadedFiles.forEach(file => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const fileData = {
        id: Date.now() + Math.random(),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadedAt: new Date(),
        url: e.target.result
      }
      files.value.push(fileData)
    }
    
    reader.readAsDataURL(file)
  })
}

const downloadFile = (file) => {
  const link = document.createElement('a')
  link.href = file.url
  link.download = file.name
  link.click()
}

const deleteFile = (fileId) => {
  files.value = files.value.filter(file => file.id !== fileId)
}

const getTotalSize = computed(() => {
  return files.value.reduce((total, file) => total + file.size, 0)
})

const getFileIcon = (type) => {
  if (type.startsWith('image/')) return '🖼️'
  if (type.startsWith('video/')) return '🎥'
  if (type.includes('pdf')) return '📄'
  if (type.includes('word') || type.includes('document')) return '📝'
  return '📎'
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString()
}
</script>

<style scoped>
.media-clean {
  /* Custom animations for the aura effect */
}

/* Enhanced hover effects */
.hover-lift {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s ease;
}

/* Transform effects */
.transform {
  transition: transform 0.3s ease;
}

.transform:hover {
  transform: scale(1.05);
}

/* Upload area styling */
.upload-area {
  border: 2px dashed #374151;
  transition: all 0.3s ease;
}

.upload-area:hover {
  border-color: #111827;
  background-color: #f9fafb;
}

/* File item hover effects */
.file-item:hover {
  background-color: #f3f4f6;
}
</style>